import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { User, Ruler, MapPin, Plus, Trash2, Save } from 'lucide-react';
import { toast } from 'sonner';

export default function ProfilePage() {
  const { isAuthenticated, user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('profile');

  useEffect(() => {
    if (!authLoading && !isAuthenticated) navigate('/login');
  }, [isAuthenticated, authLoading, navigate]);

  if (authLoading) return <div className="p-10 text-center text-muted-foreground">Loading...</div>;

  const tabs = [
    { key: 'profile', label: 'Profile', icon: User },
    { key: 'measurements', label: 'Measurements', icon: Ruler },
    { key: 'addresses', label: 'Addresses', icon: MapPin },
  ];

  return (
    <div className="max-w-4xl mx-auto px-6 md:px-10 py-16" data-testid="profile-page">
      <div className="eyebrow mb-4">Account</div>
      <h1 className="font-display font-light text-ink leading-none tracking-[-0.015em] mb-10" style={{ fontSize: 'clamp(40px, 5vw, 64px)' }}>My Account</h1>
      <div className="flex gap-8 mb-10 border-b border-border overflow-x-auto">
        {tabs.map((t) => (
          <button
            key={t.key}
            onClick={() => setActiveTab(t.key)}
            className={`flex items-center gap-2 pb-4 -mb-px uppercase tracking-luxe text-[12px] font-semibold whitespace-nowrap transition-colors ${
              activeTab === t.key ? 'text-ink border-b border-ink' : 'text-ink-muted hover:text-ink'
            }`}
            data-testid={`profile-tab-${t.key}`}
          >
            <t.icon className="w-4 h-4" /> {t.label}
          </button>
        ))}
      </div>

      {activeTab === 'profile' && <ProfileInfo user={user} />}
      {activeTab === 'measurements' && <MeasurementsTab />}
      {activeTab === 'addresses' && <AddressesTab />}
    </div>
  );
}

function ProfileInfo({ user }) {
  const [name, setName] = useState(user?.name || '');
  const [email, setEmail] = useState(user?.email || '');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/users/me', { name, email });
      toast.success('Profile updated');
    } catch { toast.error('Failed to update'); }
    finally { setSaving(false); }
  };

  return (
    <div className="max-w-sm space-y-4" data-testid="profile-info">
      <div><Label>Phone</Label><Input value={user?.phone || ''} disabled className="bg-muted" /></div>
      <div><Label>Name</Label><Input value={name} onChange={(e) => setName(e.target.value)} data-testid="profile-name" /></div>
      <div><Label>Email</Label><Input value={email} onChange={(e) => setEmail(e.target.value)} data-testid="profile-email" /></div>
      <Button onClick={handleSave} disabled={saving} className="rounded-none" data-testid="save-profile-btn"><Save className="w-4 h-4 mr-2" />{saving ? 'Saving...' : 'Save'}</Button>
    </div>
  );
}

function MeasurementsTab() {
  const [measurements, setMeasurements] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/measurements').then((res) => setMeasurements(res.data.data || [])).catch(() => {}).finally(() => setLoading(false));
    api.get('/categories').then((res) => setCategories(res.data.data || [])).catch(() => {});
  }, []);

  const getCategoryName = (slug) => {
    const cat = categories.find(c => c.slug === slug);
    return cat?.name || slug || 'General';
  };

  if (loading) return <div className="text-sm text-muted-foreground">Loading...</div>;

  return (
    <div data-testid="measurements-tab">
      {measurements.length === 0 && (
        <div className="text-center py-8">
          <Ruler className="w-8 h-8 text-muted-foreground mx-auto mb-3" />
          <p className="text-sm text-muted-foreground mb-1">No saved measurement profiles yet.</p>
          <p className="text-xs text-muted-foreground">Your measurements will be added by the admin after placing an order.</p>
        </div>
      )}

      {measurements.map((m) => (
        <div key={m.id} className="border border-border rounded-xl p-4 mb-3" data-testid={`measurement-profile-${m.id}`}>
          <div className="mb-2">
            <h3 className="font-medium text-sm">{m.name}</h3>
            <span className="text-[10px] font-medium uppercase text-primary">{getCategoryName(m.category_slug)}</span>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {Object.entries(m.measurements || {}).filter(([, val]) => val !== '' && val !== 0 && val !== null).map(([key, val]) => (
              <div key={key} className="text-xs"><span className="text-muted-foreground capitalize">{key.replace(/_/g, ' ')}:</span> <span className="font-medium">{val} in</span></div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function AddressesTab() {
  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [newAddr, setNewAddr] = useState({ label: 'home', name: '', phone: '', line1: '', line2: '', city: '', state: '', pincode: '' });

  const load = () => {
    api.get('/addresses').then((res) => setAddresses(res.data.data || [])).catch(() => {}).finally(() => setLoading(false));
  };

  useEffect(load, []);

  const handleCreate = async () => {
    if (!newAddr.name || !newAddr.phone || !newAddr.line1 || !newAddr.city || !newAddr.state || !newAddr.pincode) {
      toast.error('Fill all required fields'); return;
    }
    try {
      await api.post('/addresses', { ...newAddr, is_default: addresses.length === 0 });
      setNewAddr({ label: 'home', name: '', phone: '', line1: '', line2: '', city: '', state: '', pincode: '' });
      setShowAdd(false);
      load();
      toast.success('Address added');
    } catch { toast.error('Failed'); }
  };

  const handleDelete = async (id) => {
    try { await api.delete(`/addresses/${id}`); load(); toast.success('Deleted'); } catch { toast.error('Failed'); }
  };

  if (loading) return <div className="text-sm text-muted-foreground">Loading...</div>;

  return (
    <div data-testid="addresses-tab">
      {addresses.length === 0 && !showAdd && <p className="text-sm text-muted-foreground mb-4">No saved addresses.</p>}
      {addresses.map((a) => (
        <div key={a.id} className="border border-border rounded-xl p-4 mb-3">
          <div className="flex items-center justify-between">
            <div>
              <span className="text-[10px] font-medium uppercase text-muted-foreground">{a.label}</span>
              {a.is_default && <span className="ml-2 text-[10px] text-primary font-medium">Default</span>}
              <p className="text-sm font-medium">{a.name} &middot; {a.phone}</p>
              <p className="text-xs text-muted-foreground">{a.line1}{a.line2 ? `, ${a.line2}` : ''}, {a.city}, {a.state} - {a.pincode}</p>
            </div>
            <button onClick={() => handleDelete(a.id)} className="text-muted-foreground hover:text-destructive"><Trash2 className="w-4 h-4" /></button>
          </div>
        </div>
      ))}
      {!showAdd ? (
        <Button variant="outline" onClick={() => setShowAdd(true)} className="rounded-none" data-testid="add-address-btn"><Plus className="w-4 h-4 mr-2" /> Add Address</Button>
      ) : (
        <div className="border border-border rounded-xl p-4 space-y-3" data-testid="new-address-form-profile">
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Name *</Label><Input value={newAddr.name} onChange={(e) => setNewAddr({ ...newAddr, name: e.target.value })} /></div>
            <div><Label className="text-xs">Phone *</Label><Input value={newAddr.phone} onChange={(e) => setNewAddr({ ...newAddr, phone: e.target.value })} /></div>
          </div>
          <div><Label className="text-xs">Address Line 1 *</Label><Input value={newAddr.line1} onChange={(e) => setNewAddr({ ...newAddr, line1: e.target.value })} /></div>
          <div><Label className="text-xs">Address Line 2</Label><Input value={newAddr.line2} onChange={(e) => setNewAddr({ ...newAddr, line2: e.target.value })} /></div>
          <div className="grid grid-cols-3 gap-3">
            <div><Label className="text-xs">City *</Label><Input value={newAddr.city} onChange={(e) => setNewAddr({ ...newAddr, city: e.target.value })} /></div>
            <div><Label className="text-xs">State *</Label><Input value={newAddr.state} onChange={(e) => setNewAddr({ ...newAddr, state: e.target.value })} /></div>
            <div><Label className="text-xs">Pincode *</Label><Input value={newAddr.pincode} onChange={async (e) => {
              const v = e.target.value.replace(/\D/g, '').slice(0, 6);
              setNewAddr((p) => ({ ...p, pincode: v }));
              if (v.length === 6) {
                try {
                  const r = await fetch(`https://api.postalpincode.in/pincode/${v}`);
                  const d = await r.json();
                  if (Array.isArray(d) && d[0]?.Status === 'Success' && d[0].PostOffice?.length) {
                    const po = d[0].PostOffice[0];
                    setNewAddr((p) => ({ ...p, city: po.District || p.city, state: po.State || p.state }));
                  }
                } catch { /* silent */ }
              }
            }} maxLength={6} /></div>
          </div>
          <div className="flex gap-2">
            <Button onClick={handleCreate} className="rounded-none">Save</Button>
            <Button variant="outline" onClick={() => setShowAdd(false)} className="rounded-none">Cancel</Button>
          </div>
        </div>
      )}
    </div>
  );
}
